import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { ContactExpense, ContactExpensesRequest } from '../../models/related/contact-expense';
import { NewSupplier } from '../../models/supplier/new-supplier';
import { RelatedDataService } from '../../services/related-data.service';

type ExpenseKind = 'bill' | 'payment' | 'credit';
type ExpenseFilter = 'all' | ExpenseKind;
type ExpenseStatus = 'paid' | 'draft' | 'overdue' | 'authorised';

/** One month of the expanded view's chart. */
interface SpendMonth {
  key: string;
  label: string;
  bills: number;
  payments: number;
  credits: number;
  total: number;
}

/**
 * Contacts Side Panel - Option C: Expenses tab.
 * Renders a unified transaction ledger (Bills, Payments and Credit notes)
 * for the contact, fed by the Xero accounting integration.
 */
@Component({
  selector: 'app-contact-expenses',
  templateUrl: './contact-expenses.component.html',
  styleUrls: ['./contact-expenses.component.scss']
})
export class ContactExpensesComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() supplier: NewSupplier;
  @Input() billsToPayMode: boolean = false;
  @Output() clickInvoiceEvent = new EventEmitter<string>();

  isSpinnerShown: boolean = false;
  expenses: ContactExpense[] = [];

  activeFilter: ExpenseFilter = 'all';
  searchQuery: string = '';

  counts = { all: 0, bill: 0, payment: 0, credit: 0 };

  outstandingTotal: number = 0;
  openBillsCount: number = 0;
  overdueTotal: number = 0;
  overdueCount: number = 0;

  /**
   * The expanded view: the panel grows to the left, and the spending beside the list -- twelve
   * months of it as a chart, and what it adds up to.
   */
  expanded = false;
  @Output() expandedChange = new EventEmitter<boolean>();

  months: SpendMonth[] = [];
  monthMax = 0;
  spend12 = 0;
  monthlyAverage = 0;
  averageBill = 0;
  billCount12 = 0;
  lastPaid: string = null;
  creditTotal12 = 0;

  /** The type pill's menu. */
  typeMenuOpen = false;

  /** How many of the latest to show before "See details". */
  readonly recentCount = 5;

  readonly filters: { id: ExpenseFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'bill', label: 'Bills' },
    { id: 'payment', label: 'Expenses' },
    { id: 'credit', label: 'Credit notes' }
  ];

  constructor(
    private readonly _relatedDataService: RelatedDataService,
    private readonly _organizationService: OrganizationService,
    private readonly _toasterMessageService: ToasterMessageService,
    private readonly _el: ElementRef<HTMLElement>
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.setPanelWide(false);
    super.ngOnDestroy();
  }

  toggleExpanded(): void {
    this.expanded = !this.expanded;
    this.setPanelWide(this.expanded);
    this.expandedChange.emit(this.expanded);
  }

  /** The side panel this sits in (whichever page hosts it) grows to the left, or back. */
  private setPanelWide(wide: boolean): void {
    const panel = this._el.nativeElement.closest('.sidenav');
    if (panel) { panel.classList.toggle('sidenav--wide', wide); }
  }

  barHeight(value: number): number {
    return this.monthMax > 0 ? Math.max(0, Math.round((value / this.monthMax) * 100)) : 0;
  }

  monthTitle(m: SpendMonth): string {
    const money = (v: number) => '$' + v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${m.label}: ${money(m.total)}` + (m.bills ? ` · bills ${money(m.bills)}` : '')
      + (m.payments ? ` · expenses ${money(m.payments)}` : '') + (m.credits ? ` · credits -${money(m.credits)}` : '');
  }

  ngOnInit(): void {
    if (!this.supplier?.supplierId) {
      return;
    }

    this.isSpinnerShown = true;

    const request = {
      organizationId: this._organizationService.selectedOrganization,
      contactId: this.supplier.supplierId
    } as ContactExpensesRequest;

    this._relatedDataService.getContactExpenses(request)
      .pipe(this.untilThis)
      .subscribe(
        (data: ContactExpense[]) => {
          this.isSpinnerShown = false;
          this.expenses = data || [];
          this.computeSnapshot();
          this.computeCounts();
          this.computeInsights();
        },
        (e: Error) => {
          this.isSpinnerShown = false;
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  get filteredExpenses(): ContactExpense[] {
    const q = this.searchQuery?.trim().toLowerCase();
    return this.expenses.filter(e => {
      const matchesKind = this.activeFilter === 'all' || this.kindOf(e) === this.activeFilter;
      if (!matchesKind) {
        return false;
      }
      if (!q) {
        return true;
      }
      const haystack = [e.number, e.reference, e.total?.toString()].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }

  setFilter(filter: ExpenseFilter): void {
    this.activeFilter = filter;
    this.typeMenuOpen = false;
  }

  get activeFilterLabel(): string {
    return this.activeFilter === 'all' ? 'All types' : this.filters.find(f => f.id === this.activeFilter)?.label;
  }

  /** The latest few, newest first, whatever their type: the panel's summary. */
  get recentExpenses(): ContactExpense[] {
    return [...this.expenses]
      .sort((a, b) => (new Date(b.date).getTime() || 0) - (new Date(a.date).getTime() || 0))
      .slice(0, this.recentCount);
  }

  /** A chart value as it fits above a bar: "$1.2k", "$450". */
  shortMoney(value: number): string {
    const v = Math.round(value || 0);
    if (!v) { return ''; }
    if (Math.abs(v) >= 1000000) { return '$' + (v / 1000000).toFixed(1).replace(/\.0$/, '') + 'm'; }
    if (Math.abs(v) >= 1000) { return '$' + (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'; }
    return '$' + v;
  }

  kindOf(e: ContactExpense): ExpenseKind {
    switch (e.transactionType) {
      case 'CreditNote': return 'credit';
      case 'BankTransaction': return 'payment';
      default: return 'bill';
    }
  }

  /** Display status used to pick the badge style and label. */
  displayStatus(e: ContactExpense): ExpenseStatus {
    const status = (e.status || '').toUpperCase();
    if (status === 'PAID' || this.kindOf(e) === 'payment') {
      return 'paid';
    }
    if (status === 'DRAFT' || status === 'SUBMITTED') {
      return 'draft';
    }
    if (this.isOverdue(e)) {
      return 'overdue';
    }
    return 'authorised';
  }

  statusLabel(e: ContactExpense): string {
    return { paid: 'Paid', draft: 'Draft', overdue: 'Overdue', authorised: 'Authorised' }[this.displayStatus(e)];
  }

  /** Secondary line under the type: paid date, due date or reference. */
  metaLine(e: ContactExpense): string {
    if (this.displayStatus(e) === 'paid') {
      const paidOn = e.fullyPaidOnDate || e.date;
      return paidOn ? `Paid ${this.formatDate(paidOn)}` : 'Paid';
    }
    if (e.dueDate) {
      return `Due ${this.formatDate(e.dueDate)}`;
    }
    return e.reference || '';
  }

  kindLabel(e: ContactExpense): string {
    return { bill: 'Bill', payment: 'Expenses', credit: 'Credit note' }[this.kindOf(e)];
  }

  /** Credit notes reduce the balance, so they render as a negative amount. */
  signedTotal(e: ContactExpense): number {
    const total = e.total || 0;
    return this.kindOf(e) === 'credit' ? -total : total;
  }

  absTotal(e: ContactExpense): number {
    return Math.abs(this.signedTotal(e));
  }

  openExpense(e: ContactExpense): void {
    // Only Bills/Invoices have a detail view today. Emit the transaction so the
    // host panel can load it in place, mirroring how the Company opens within
    // the Inbox detail view.
    if (this.kindOf(e) !== 'bill') {
      return;
    }
    this.clickInvoiceEvent.emit(e.transactionId);
  }

  private isOverdue(e: ContactExpense): boolean {
    if (!e.dueDate || !(e.amountDue > 0)) {
      return false;
    }
    const due = new Date(e.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due < today;
  }

  private computeSnapshot(): void {
    const openBills = this.expenses.filter(e => this.kindOf(e) === 'bill' && e.amountDue > 0);
    this.openBillsCount = openBills.length;
    this.outstandingTotal = openBills.reduce((sum, b) => sum + (b.amountDue || 0), 0);

    const overdue = openBills.filter(b => this.isOverdue(b));
    this.overdueCount = overdue.length;
    this.overdueTotal = overdue.reduce((sum, b) => sum + (b.amountDue || 0), 0);
  }

  /** Twelve months to this one: what was spent with the contact, month by month. */
  private computeInsights(): void {
    const now = new Date();
    const months: SpendMonth[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push({
        key: `${d.getFullYear()}-${d.getMonth()}`,
        label: d.toLocaleDateString('en-AU', { month: 'short' }) + (d.getMonth() === 0 || i === 11 ? ' ' + String(d.getFullYear()).slice(2) : ''),
        bills: 0, payments: 0, credits: 0, total: 0
      });
    }
    const byKey = new Map(months.map(m => [m.key, m]));
    let billTotal = 0;
    this.billCount12 = 0;
    let lastPaid: Date = null;

    this.expenses.forEach(e => {
      const date = new Date(e.date);
      if (isNaN(date.getTime())) { return; }
      const m = byKey.get(`${date.getFullYear()}-${date.getMonth()}`);
      const kind = this.kindOf(e);
      const amount = e.total || 0;
      if (m) {
        if (kind === 'bill') { m.bills += amount; billTotal += amount; this.billCount12++; }
        if (kind === 'payment') { m.payments += amount; }
        if (kind === 'credit') { m.credits += amount; }
      }
      if (this.displayStatus(e) === 'paid') {
        const paid = new Date(e.fullyPaidOnDate || e.date);
        if (!isNaN(paid.getTime()) && (!lastPaid || paid > lastPaid)) { lastPaid = paid; }
      }
    });

    months.forEach(m => m.total = m.bills + m.payments - m.credits);
    this.months = months;
    this.monthMax = Math.max(0, ...months.map(m => m.bills + m.payments));
    this.spend12 = months.reduce((sum, m) => sum + m.total, 0);
    this.creditTotal12 = months.reduce((sum, m) => sum + m.credits, 0);
    this.monthlyAverage = this.spend12 / 12;
    this.averageBill = this.billCount12 ? billTotal / this.billCount12 : 0;
    this.lastPaid = lastPaid ? this.formatDate(lastPaid.toISOString()) : null;
  }

  private computeCounts(): void {
    this.counts = { all: this.expenses.length, bill: 0, payment: 0, credit: 0 };
    this.expenses.forEach(e => this.counts[this.kindOf(e)]++);
  }

  private formatDate(value: string): string {
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      return '';
    }
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
  }
}
